import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useLenis } from "lenis/react";
import { ArrowDown } from "lucide-react";
import { IMAGES, STATS } from "../../data";

const LINES = ["FITNESS FOR", "THE 4AM", "LIFESTYLE"];

const lineParent = {
  hidden: {},
  show: { transition: { staggerChildren: 0.14, delayChildren: 0.3 } },
};
const lineChild = {
  hidden: { y: "110%" },
  show: { y: "0%", transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1] } },
};

export const Hero = () => {
  const ref = useRef(null);
  const lenis = useLenis();
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.2]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  const go = (id) => {
    const el = document.getElementById(id);
    if (el && lenis) lenis.scrollTo(el, { offset: -20 });
  };

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-[100svh] flex flex-col justify-end overflow-hidden"
    >
      <motion.div
        style={{ y: bgY, scale: bgScale }}
        className="absolute inset-0 z-0"
      >
        <img
          src={IMAGES.heroBg}
          alt="Las Vegas neon skyline at night"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/60" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/40 to-black/30" />
      </motion.div>

      <motion.div
        style={{ opacity: fade }}
        className="relative z-10 mx-auto w-full max-w-[1400px] px-5 md:px-10 pb-16 md:pb-24 pt-32"
      >
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.1, duration: 0.6 }}
          className="flex items-center gap-3 mb-6"
        >
          <span className="h-2 w-2 rounded-full bg-[#e5ff00] neon-text" />
          <span className="text-[11px] md:text-xs uppercase tracking-[0.32em] text-white/70">
            The Nightlife Recomp Specialist · Las Vegas
          </span>
        </motion.div>

        <motion.h1
          variants={lineParent}
          initial="hidden"
          animate="show"
          className="font-display uppercase leading-[0.86] text-white text-6xl sm:text-7xl md:text-8xl lg:text-[10rem]"
        >
          {LINES.map((line, i) => (
            <span key={i} className="block overflow-hidden">
              <motion.span
                variants={lineChild}
                className={`block ${i === 1 ? "text-[#e5ff00]" : ""}`}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </motion.h1>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.2, duration: 0.8 }}
          className="mt-8 flex flex-col md:flex-row md:items-end md:justify-between gap-8"
        >
          <p className="max-w-md text-base md:text-lg text-white/70 leading-relaxed">
            Fitness and nutrition built specifically for the 4AM lifestyle. No
            9-to-5 rules. Just tactics that work while you close the bar,
            work the floor, and live on Vegas time.
          </p>
          <button
            data-testid="hero-cta"
            onClick={() => go("booking")}
            className="group shrink-0 inline-flex items-center gap-3 bg-[#e5ff00] text-black uppercase tracking-[0.18em] text-sm font-bold px-7 py-4 rounded-full hover:neon-glow transition-shadow duration-300"
          >
            Book a Consult
            <ArrowDown
              size={18}
              className="group-hover:translate-y-1 transition-transform duration-300"
            />
          </button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5, duration: 0.8 }}
          className="mt-14 grid grid-cols-3 gap-4 border-t border-white/10 pt-8 max-w-2xl"
        >
          {STATS.map((s) => (
            <div key={s.label} data-testid="hero-stat">
              <div className="font-display text-3xl md:text-5xl text-white leading-none">
                {s.value}
              </div>
              <div className="mt-2 text-[10px] md:text-xs uppercase tracking-[0.18em] text-white/50">
                {s.label}
              </div>
            </div>
          ))}
        </motion.div>
      </motion.div>
    </section>
  );
};
